import {H3} from '@jetbrains/ring-ui/components/heading/heading'
import classNames from 'classnames'
import {memo, useCallback, useMemo} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import {useBranch, useQueryParam} from '../../../../../hooks/routes'
import {getHrefWithQueryParams} from '../../../../../routes'
import {restApi} from '../../../../../services/rest'
import type {BuildTypeId, ProjectId, TestId, TestOccurrenceId} from '../../../../../types'
import {stringifyId} from '../../../../../types'
import {emptyArray} from '../../../../../utils/empty'
import Grid from '../../../../common/Grid/Grid'
import RowGroup from '../../../../common/Grid/RowGroup'
import Pager from '../../../../common/Pager/Pager.container'
import {usePage} from '../../../../common/Pager/Pager.hooks'
import {PagerGroup} from '../../../../common/Pager/Pager.types'
import TestHistoryPagerWarning from '../../../../common/Pager/TestPager/TestHistoryPagerWarning'
import {useSequenceLoaderReadyHandler} from '../../../../common/SequenceLoader/SequenceLoader.hooks'
import {useTestOccurencesArg} from '../../../../packages/Tests/Tests.rest'
import {makeGetTestOccurrencesList} from '../../../../packages/Tests/Tests.selectors'
import {getTestsLocator} from '../../../BuildPage/BuildTestsTab/BuildTestsTab.utils'
import {TestFilterStatus} from '../../../BuildPage/BuildTestsTab/TestsFilter/TestsStatusFilter/TestsStatusFilter.utils'

import TestHistoryItem from './TestHistoryItem/TestHistoryItem'
import {getFilterQueryParams} from './TestHistoryList.utils'
import TestHistoryListHeader from './TestHistoryListHeader/TestHistoryListHeader'

import styles from './TestHistoryList.css'

import {useAppSelector} from 'src/hooks/react-redux'

const COUNT_VISIBLE_TESTS = 100
const OPTIONS = {
  withInvocationsCounters: true,
  withUpdatePager: true,
  withBuildInfo: true,
}

type Props = {
  projectId: ProjectId
  locator: string
}

function TestHistoryList({projectId, locator}: Props) {
  const location = useLocation()
  const navigate = useNavigate()
  const getTestOccurrencesList = useMemo(makeGetTestOccurrencesList, [])

  const arg = useTestOccurencesArg(locator, OPTIONS)
  const {ids, loading} = restApi.endpoints.getAllTestOccurrencesNormalized.useQuery(arg, {
    selectFromResult: ({data, currentData}) => ({
      ids: data?.data.result ?? emptyArray,
      loading: data !== currentData,
    }),
  })
  const testOccurrences = useAppSelector(state => getTestOccurrencesList(state, ids))

  const onChangeQueryParams = useCallback(
    (newParams: Record<string, string | null | undefined>) =>
      navigate(getHrefWithQueryParams(location, null, oldParams => ({...oldParams, ...newParams}))),
    [location, navigate],
  )

  const expandedTest = useQueryParam('expandedTest')

  const onExpandedTest = useCallback(
    (id: TestOccurrenceId) =>
      onChangeQueryParams({expandedTest: expandedTest === id ? null : stringifyId(id)}),
    [expandedTest, onChangeQueryParams],
  )

  return (
    <div className={styles.wrapper}>
      <H3 className={styles.heading}>{'History'}</H3>
      <div className={classNames(styles.content, {[styles.withOverlay]: loading})}>
        <Grid className={styles.grid}>
          <RowGroup span={1}>
            <TestHistoryListHeader
              {...getFilterQueryParams(location)}
              onChangeQueryParams={onChangeQueryParams}
            />
          </RowGroup>
          <div className={styles.list}>
            {testOccurrences.map(({id}) => (
              <TestHistoryItem
                key={stringifyId(id)}
                testOccurrenceId={id}
                projectId={projectId}
                expanded={expandedTest === id}
                onExpandedTest={onExpandedTest}
              />
            ))}
          </div>
        </Grid>
      </div>
      <Pager
        group={PagerGroup.TEST}
        warningComponent={TestHistoryPagerWarning}
        isLoading={loading}
      />
    </div>
  )
}

type WrapperProps = {
  testId: TestId
  projectId: ProjectId
  buildTypeId: BuildTypeId | null | undefined
  seqKey?: string
}

function TestHistoryListWrapper({testId, projectId, buildTypeId}: WrapperProps) {
  const location = useLocation()
  const branch = useBranch()

  const baseLocator =
    buildTypeId != null
      ? `buildType:${stringifyId(buildTypeId)},test:(id:${stringifyId(testId)})`
      : `affectedProject:${stringifyId(projectId)},test:(id:${stringifyId(testId)})`

  const {orderBy, order} = getFilterQueryParams(location)
  const currentPage = usePage()

  const locator = getTestsLocator({
    baseLocator,
    branch,
    currentPage,
    orderBy,
    order,
    status: TestFilterStatus.ALL,
    count: COUNT_VISIBLE_TESTS,
  })

  const arg = useTestOccurencesArg(locator, OPTIONS)
  const {ready} = restApi.endpoints.getAllTestOccurrencesNormalized.useQuery(arg, {
    selectFromResult: ({data}) => ({ready: data != null}),
  })

  useSequenceLoaderReadyHandler(ready)

  return <TestHistoryList key={locator} projectId={projectId} locator={locator} />
}

export default memo<WrapperProps>(TestHistoryListWrapper)
